import React, { useState } from 'react';
import { useAbdm } from '../context/AbdmContext';
import { handleSelectAccountAndFetchProfile } from '../../utils/api';

export const AccountSelector: React.FC = () => {
    const { state, dispatch } = useAbdm();
    const { availableAccounts, selectedAccount, transactionId, authData, authConfig, tToken } = state;
    const [otpLoading, setOtpLoading] = useState(false);
    const [otpError, setOtpError] = useState('');

    const onSelectAccount = async () => {
        setOtpLoading(true);
        setOtpError('');

        if (authConfig.type === 'communication-mobile' && !tToken) {
            setOtpError('Missing tToken for user verification.');
            setOtpLoading(false);
            return;
        }

        try {
            const { profile, xToken } = await handleSelectAccountAndFetchProfile(
                authData!,
                transactionId,
                selectedAccount!,
                authConfig,
                tToken
            );
            dispatch({ type: 'SET_X_TOKEN', payload: xToken });

            // If profile is partial (e.g. missing photo) and we have xToken, try to fetch full profile
            // This is especially for flows where select-account returns minimal info
            // If profile is partial (e.g. missing photo) and we have xToken, try to fetch full profile
            // This is already handled in handleSelectAccountAndFetchProfile in api.ts now.
            // keeping this clean.

            dispatch({ type: 'SET_USER_PROFILE', payload: profile });
            dispatch({ type: 'SET_STEP', payload: 'success' });
        } catch (error) {
            setOtpError((error as Error).message);
        } finally {
            setOtpLoading(false);
        }
    };

    const handleResetFlow = () => {
        dispatch({ type: 'SET_T_TOKEN', payload: '' });
        dispatch({ type: 'RESET_FLOW' });
    };

    const onAccountClick = (account: typeof availableAccounts[0]) => {
        dispatch({ type: 'SET_SELECTED_ACCOUNT', payload: account });
    };

    if (!authData || availableAccounts.length === 0) return null;

    return (
        <div className="step-container">
            <h2>Select Your ABHA Account</h2>
            <div className="transaction-info flow-info">
                <p><strong>Multiple accounts linked to:</strong> <code>{authConfig.value}</code></p>
                <p className="text-secondary">Choose the one you'd like to use for seamless health record access.</p>
            </div>

            <div className="suggestions-list accounts-grid">
                {availableAccounts.map((account, index) => {
                    const isSelected = selectedAccount?.preferredAbhaAddress === account.preferredAbhaAddress;
                    return (
                        <div
                            key={index}
                            className={`suggestion-item account-card ${isSelected ? 'selected' : ''} hover-lift`}
                            onClick={() => onAccountClick(account)}
                        >
                            <div className="account-preview">
                                {/* Enhanced Profile Photo with Ring */}
                                <div className="photo-container">
                                    {account.profilePhoto ? (
                                        <img
                                            src={`data:image/jpeg;base64,${account.profilePhoto}`}
                                            alt={`${account.name}'s profile`}
                                            className="account-photo"
                                            loading="lazy"
                                        />
                                    ) : (
                                        <div className="default-avatar-account">
                                            <span className="avatar-initial">{account.name.charAt(0).toUpperCase()}</span>
                                        </div>
                                    )}
                                    {isSelected && <div className="selection-ring"></div>}
                                </div>
                                {/* Enhanced Account Details */}
                                <div className="account-details">
                                    <div className="account-header">
                                        <strong className="account-name">{account.name}</strong>
                                        {isSelected && <span className="checkmark">✓</span>}
                                    </div>
                                    <div className="abha-address">
                                        <small className="abha-number">{account.preferredAbhaAddress}</small>
                                    </div>
                                    {account.dob && (
                                        <div className="account-meta">
                                            <small className="dob">DOB: {account.dob}</small>
                                        </div>
                                    )}
                                </div>
                            </div>
                            <input
                                type="radio"
                                name="abhaAccount"
                                checked={isSelected}
                                onChange={() => onAccountClick(account)}
                                className="sr-only" // Screen reader only for accessibility
                            />
                        </div>
                    );
                })}
            </div>

            <div className="action-buttons">
                <button
                    onClick={onSelectAccount}
                    disabled={otpLoading || !selectedAccount}
                    className="btn-primary"
                    aria-label={otpLoading ? 'Loading profile details' : 'Select account and continue'}
                >
                    {otpLoading ? (
                        <span className="loading">🔄 Loading Profile...</span>
                    ) : (
                        `Select & Continue →`
                    )}
                </button>
                <button onClick={handleResetFlow} className="btn-outline" aria-label="Start a new flow">
                    ← New Flow
                </button>
            </div>
            {otpError && (
                <div className="error-message" role="alert">
                    <span className="error-icon">⚠️</span> {otpError}
                </div>
            )}
        </div>
    );
};